import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { partnerAuthService } from '../services/partnerAuth.service';
import type { PartnerSummary } from '../types';

interface PartnerAuthContextValue {
  partner: PartnerSummary | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setPartner: (partner: PartnerSummary | null) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const PartnerAuthContext = createContext<PartnerAuthContextValue | undefined>(
  undefined,
);

export const PartnerAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [partner, setPartnerState] = useState<PartnerSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const setPartner = useCallback((value: PartnerSummary | null) => {
    setPartnerState(value);
  }, []);

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await partnerAuthService.me();
      setPartnerState(data);
    } catch {
      setPartnerState(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await partnerAuthService.logout();
    } finally {
      setPartnerState(null);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo<PartnerAuthContextValue>(
    () => ({
      partner,
      isAuthenticated: !!partner,
      isLoading,
      setPartner,
      refresh,
      logout,
    }),
    [partner, isLoading, setPartner, refresh, logout],
  );

  return (
    <PartnerAuthContext.Provider value={value}>
      {children}
    </PartnerAuthContext.Provider>
  );
};

export const usePartnerAuth = (): PartnerAuthContextValue => {
  const ctx = useContext(PartnerAuthContext);
  if (!ctx) {
    throw new Error('usePartnerAuth must be used inside PartnerAuthProvider');
  }
  return ctx;
};
